import { Button } from 'antd';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';

import { obituaryService } from 'config/services';

import { HttpError } from 'helpers/http';

import { Obituary } from 'types/services/obituary';

import { Footer } from './Footer';
import { Header } from './Header';

export const FrontSingleView = () => {
  const { id } = useParams<{ id?: string }>();
  const [formattedString, setFormattedString] = useState('');
  const [formattedStringBereaved, setFormattedStringBereaved] = useState('');
  const {
    isLoading,
    data: obituary,
    refetch,
  } = useQuery<Obituary, HttpError>(['getObituary', id], () => obituaryService.getObituaryById(parseInt(id || '')), {
    enabled: typeof id !== 'undefined',
  });

  const [message, setMessage] = useState('');

  useEffect(() => {
    const regex = /(<([^>]+)>)/gi;
    if (obituary && obituary.main_text && obituary.main_text.match(regex)) {
      setFormattedString(obituary.main_text.replace(regex, ' ').replace(new RegExp('\r?\n', 'g'), '<br />'));
    }
    if (obituary && obituary.bereaved && obituary.bereaved.match(regex)) {
      setFormattedStringBereaved(obituary.bereaved.replace(regex, ' ').replace(new RegExp('\r?\n', 'g'), '<br />'));
    }
  }, [obituary]);

  const useMediaQuery = (width: number) => {
    const [targetReached, setTargetReached] = useState(false);

    const updateTarget = useCallback((e) => {
      if (e.matches) {
        setTargetReached(true);
      } else {
        setTargetReached(false);
      }
    }, []);

    useEffect(() => {
      const media = window.matchMedia(`(max-width: ${width}px)`);
      media.addEventListener('change', updateTarget);
      // Check on mount (callback is not called until a change occurs)
      if (media.matches) {
        setTargetReached(true);
      }

      return () => media.removeEventListener('change', updateTarget);
    }, []);

    return targetReached;
  };

  const isBreakpoint = useMediaQuery(768);

  type ContainerProps = {
    classNames?: string;
    isAvazTv?: boolean;
    width?: string;
    height?: string;
    articleType?: string;
  };

  const FacebookIcon = ({ classNames = 'h-6 w-6' }: ContainerProps) => (
    <svg
      role="img"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className={classNames}
      style={{ width: '20px' }}
    >
      <title>Facebook</title>
      <path
        fill="#333"
        d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
      />
    </svg>
  );

  // const TwitterIcon = ({ classNames = 'h-6 w-6' }: ContainerProps) => (
  //   <svg
  //     role="img"
  //     viewBox="0 0 24 24"
  //     xmlns="http://www.w3.org/2000/svg"
  //     className={classNames}
  //     style={{ width: '20px' }}
  //   >
  //     <title>Twitter</title>
  //     <path
  //       fill="#333"
  //       d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"
  //     />
  //   </svg>
  // );

  const TwitterIcon = ({ classNames = 'h-6 w-6' }: ContainerProps) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      version="1.1"
      width="20px"
      height="20px"
      viewBox="0 0 256 256"
      xmlSpace="preserve"
    >
      <defs></defs>
      <g
        stroke="none"
        strokeWidth={0}
        strokeDasharray="none"
        strokeLinecap="butt"
        strokeLinejoin="miter"
        strokeMiterlimit={10}
        fill="none"
        fillRule="nonzero"
        opacity={1}
        //style="stroke: none; stroke-width: 0; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: none; fill-rule: nonzero; opacity: 1;"
        transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)"
      >
        <polygon
          stroke="none"
          strokeWidth={1}
          strokeDasharray="none"
          strokeLinecap="butt"
          strokeLinejoin="miter"
          strokeMiterlimit={10}
          fill="rgb(0,0,0)"
          fillRule="nonzero"
          opacity={1}
          points="24.89,23.01 57.79,66.99 65.24,66.99 32.34,23.01 "
          //style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(0,0,0); fill-rule: nonzero; opacity: 1;"
          transform="  matrix(1 0 0 1 0 0) "
        />
        <path
          stroke="none"
          strokeWidth={1}
          strokeDasharray="none"
          strokeLinejoin="miter"
          strokeMiterlimit={10}
          fill="#333"
          fillRule="nonzero"
          opacity={1}
          d="M 45 0 L 45 0 C 20.147 0 0 20.147 0 45 v 0 c 0 24.853 20.147 45 45 45 h 0 c 24.853 0 45 -20.147 45 -45 v 0 C 90 20.147 69.853 0 45 0 z M 56.032 70.504 L 41.054 50.477 L 22.516 70.504 h -4.765 L 38.925 47.63 L 17.884 19.496 h 16.217 L 47.895 37.94 l 17.072 -18.444 h 4.765 L 50.024 40.788 l 22.225 29.716 H 56.032 z"
          //style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: rgb(0,0,0); fill-rule: nonzero; opacity: 1;"
          transform=" matrix(1 0 0 1 0 0) "
          strokeLinecap="round"
        />
      </g>
    </svg>
  );

  const WhatsAppIcon = ({ classNames = 'h-6 w-6' }: ContainerProps) => (
    <svg
      role="img"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: '20px' }}
      className={classNames}
    >
      <title>WhatsApp</title>
      <path
        fill="#333"
        d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
      />
    </svg>
  );

  const ViberIcon = ({ classNames = 'h-6 w-6' }: ContainerProps) => (
    <svg role="img" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ width: '20px' }}>
      <title>Viber</title>
      <path
        fill="#333"
        d="M11.4 0C9.473.028 5.333.344 3.02 2.467 1.302 4.187.696 6.7.633 9.817.57 12.933.488 18.776 6.12 20.36h.003l-.004 2.416s-.037.977.61 1.177c.777.242 1.234-.5 1.98-1.302.407-.44.972-1.084 1.397-1.58 3.85.326 6.812-.416 7.15-.525.776-.252 5.176-.816 5.892-6.657.74-6.02-.36-9.83-2.34-11.546-.596-.55-3.006-2.3-8.375-2.323 0 0-.395-.025-1.037-.017zm.058 1.693c.545-.004.88.017.88.017 4.542.02 6.717 1.388 7.222 1.846 1.675 1.435 2.53 4.868 1.906 9.897v.002c-.604 4.878-4.174 5.184-4.832 5.395-.28.09-2.882.737-6.153.524 0 0-2.436 2.94-3.197 3.704-.12.12-.26.167-.352.144-.13-.033-.166-.188-.165-.414l.02-4.018c-4.762-1.32-4.485-6.292-4.43-8.895.054-2.604.543-4.738 1.996-6.173 1.96-1.773 5.474-2.018 7.11-2.03zm.38 2.602c-.167 0-.303.135-.304.302 0 .167.133.303.3.305 1.624.01 2.946.537 4.028 1.592 1.073 1.046 1.62 2.468 1.633 4.334.002.167.14.3.307.3.166-.002.3-.138.3-.304-.014-1.984-.618-3.596-1.816-4.764-1.19-1.16-2.692-1.753-4.447-1.765zm-3.96.695c-.19-.032-.4.005-.616.117l-.01.002c-.43.247-.816.562-1.146.932-.002.004-.006.004-.008.008-.267.323-.42.638-.46.948-.008.046-.01.093-.007.14 0 .136.022.27.065.4l.013.01c.135.48.473 1.276 1.205 2.604.42.768.903 1.5 1.446 2.186.27.344.56.673.87.984l.132.132c.31.308.64.6.984.87.686.543 1.418 1.027 2.186 1.447 1.328.733 2.126 1.07 2.604 1.206l.01.014c.13.042.265.064.402.063.046.002.092 0 .138-.008.31-.036.627-.19.948-.46.004 0 .003-.002.008-.005.37-.33.683-.72.93-1.148l.003-.01c.225-.432.15-.842-.18-1.12-.004 0-.698-.58-1.037-.83-.36-.255-.73-.492-1.113-.71-.51-.285-1.032-.106-1.248.174l-.447.564c-.23.283-.657.246-.657.246-3.12-.796-3.955-3.955-3.955-3.955s-.037-.426.248-.656l.563-.448c.277-.215.456-.737.17-1.248-.217-.383-.454-.756-.71-1.115-.25-.34-.826-1.033-.83-1.035-.137-.165-.31-.265-.502-.297zm4.49.88c-.158.002-.29.124-.3.282-.01.167.115.312.282.324 1.16.085 2.017.466 2.645 1.15.63.688.93 1.524.906 2.57-.002.168.13.306.3.31.166.003.305-.13.31-.297.025-1.175-.334-2.193-1.067-2.994-.74-.81-1.777-1.253-3.05-1.346h-.024zm.463 1.63c-.16.002-.29.127-.3.287-.008.167.12.31.288.32.523.028.875.175 1.113.422.24.245.388.62.416 1.164.01.167.15.295.318.287.167-.008.295-.15.287-.317-.03-.644-.215-1.178-.58-1.557-.367-.378-.893-.574-1.52-.607h-.018z"
      />
    </svg>
  );

  const ShareIcon = ({ classNames = 'h-6 w-6' }: ContainerProps) => (
    <svg
      role="img"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      className="iosShareButton"
    >
      <title>Share Icon</title>
      <g style={{ transform: 'translate(14%, 20%)' }}>
        <path
          d="M10.0607 0.467041V4.38981C6.49756 4.38992 0 4.42901 0 15.5329C0.736667 8.10757 4.12081 8.06088 10.0607 8.0608V12.3447L16 6.4054L10.0607 0.467041Z"
          fill="#333"
        />
      </g>
    </svg>
  );

  const LinkIcon = ({ classNames = 'h-6 w-6' }: ContainerProps) => (
    <svg
      className={classNames}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '20px',
        height: '20px',
        marginTop: '6px',
        marginLeft: '2px',
      }}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
        id="Capa_4"
        color="#333"
      ></path>
    </svg>
  );
  var shareLink = window.location.href;

  const handleClick = () => {
    const currentUrl = window.location.href;

    navigator.clipboard
      .writeText(currentUrl)
      .then(() => {
        setMessage('Link kopiran!');
        setTimeout(() => {
          setMessage('');
        }, 3000);
      })
      .catch((err) => {
        console.error('Failed to copy URL: ', err);
      });
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator
        .share({
          title: document.title,
          url: window.location.href,
        })
        .then(() => console.log('Successfully shared'))
        .catch((error) => console.error('Error sharing:', error));
    } else {
      console.log('Web Share API not supported.');
    }
  };

  return (
    <div className="">
      <Header></Header>
      <div className="buttons-navigation">
        <Button
          type="text"
          id="allObituaries"
          className={`allObituaries`}
          onClick={() => {
            window.location.href = '/';
          }}
        >
          SVE
        </Button>
        <Button
          type="text"
          className={`deathObituaries `}
          id="deathObituaries"
          onClick={() => {
            window.location.href = '/?tip=umrli';
          }}
        >
          UMRLI
        </Button>
        <Button
          type="text"
          className={`remeberingObituaries `}
          id="remeberingObituaries"
          onClick={() => {
            window.location.href = '/?tip=sjecanja';
          }}
        >
          SJEĆANJA
        </Button>
        <Button
          type="text"
          className={`lastgoodbye`}
          id="lastgoodbye"
          onClick={() => {
            window.location.href = '/?tip=sucuti';
          }}
        >
          SUĆUTI
        </Button>

        {isBreakpoint && (
          <Button className="listItem small-obituary-create" href={'/objavi'} style={{ padding: '5px 10px' }}>
            <span id="publish-obituary-mobile">OBJAVITE SMRTOVNICU</span>
          </Button>
        )}
      </div>
      {!isLoading && (
        <div className="mainFrontSingleContainer">
          <div className="outline-front">
            <div className="singleObituary">
              {obituary?.personal_message?.length && obituary.personal_message !== '' ? (
                <>
                  <span className="innerElement introductory_text_style">{obituary?.personal_message}</span>
                  <br></br>
                </>
              ) : (
                <></>
              )}
              <span
                className="innerElement introductory_text_style"
                dangerouslySetInnerHTML={{
                  __html: obituary?.introductory_text?.replace(new RegExp('\r?\n', 'g'), '<br />') || '',
                }}
              >
                {/* {obituary?.introductory_text} */}
              </span>
              <br></br>
              {obituary?.people_id.length === 1 ? (
                <div>
                  <span className="innerElement obituaryImage">
                    {obituary?.people_id[0].filename && (
                      <img
                        src={obituary?.people_id[0].filename}
                        alt="1"
                        style={{ width: '87px', height: '120px' }}
                      ></img>
                    )}
                  </span>
                  <br></br>
                  <span className="innerElement fullName">
                    {obituary?.people_id[0].firstName}{' '}
                    {obituary?.people_id[0].additional_name ? '(' + obituary?.people_id[0].additional_name + ')' : ''}{' '}
                    {obituary?.people_id[0].lastName}{' '}
                    {obituary.people_id[0].maiden_name ? ', ROĐ. ' + obituary.people_id[0].maiden_name : ''}
                  </span>
                  {/* {obituary?.people_id[0].city && <span className="city">{obituary?.people_id[0].city}</span>} */}
                  <br></br>
                  <span className="innerElement obituaryDate">
                    {obituary?.people_id[0].birth_date
                      ? DateTime.fromISO(String(obituary?.people_id[0].birth_date)).toLocaleString(DateTime.DATE_SHORT)
                      : obituary?.people_id[0].birth_approx}
                    {(obituary?.people_id[0].birth_date && obituary?.people_id[0].birth_end) ||
                    (obituary?.people_id[0].birth_approx && obituary?.people_id[0].birth_end_approx) ||
                    (obituary.people_id[0].birth_date && obituary.people_id[0].birth_end_approx) ||
                    (obituary.people_id[0].birth_approx && obituary.people_id[0].birth_end)
                      ? ' - '
                      : ''}
                    {obituary?.people_id[0].birth_end
                      ? DateTime.fromISO(String(obituary?.people_id[0].birth_end)).toLocaleString(DateTime.DATE_SHORT)
                      : obituary?.people_id[0].birth_end_approx}
                  </span>
                </div>
              ) : obituary?.people_id !== undefined && obituary?.people_id.length > 1 ? (
                <div className="obituary-wrapper">
                  {obituary?.people_id.map((p, i) => {
                    return (
                      <div className="obituary-child">
                        <span className="innerElement">
                          {obituary?.people_id[i].filename && (
                            <img
                              src={obituary?.people_id[i].filename}
                              alt="/1.png"
                              style={{ width: '87px', height: '120px' }}
                            ></img>
                          )}
                        </span>
                        <br></br>
                        <span className="innerElement fullName">
                          {obituary?.people_id[i].firstName}{' '}
                          {obituary?.people_id[i].additional_name
                            ? '(' + obituary?.people_id[i].additional_name + ')'
                            : ''}{' '}
                          {obituary?.people_id[i].lastName}{' '}
                          {obituary.people_id[i].maiden_name ? ', ROĐ. ' + obituary.people_id[i].maiden_name : ''}
                        </span>
                        {/* {obituary.people_id[i].city && <span className="city">{obituary.people_id[i].city}</span>} */}
                        {obituary?.people_id[i].birth_date ||
                        obituary?.people_id[i].birth_approx ||
                        obituary?.people_id[i].birth_end_approx ||
                        obituary?.people_id[i].birth_end ? (
                          <br />
                        ) : (
                          <></>
                        )}
                        <span className="innerElement bottom-date obituaryDate">
                          {obituary?.people_id[i].birth_date
                            ? DateTime.fromISO(String(obituary?.people_id[i].birth_date)).toLocaleString(
                                DateTime.DATE_SHORT,
                              )
                            : obituary?.people_id[i].birth_approx}
                          {(obituary?.people_id[i].birth_date ||
                            obituary?.people_id[i].birth_end ||
                            obituary?.people_id[i].birth_approx ||
                            obituary?.people_id[i].birth_end_approx) &&
                            ' - '}
                          {obituary?.people_id[i].birth_end
                            ? DateTime.fromISO(String(obituary?.people_id[i].birth_end)).toLocaleString(
                                DateTime.DATE_SHORT,
                              )
                            : obituary?.people_id[i].birth_end_approx}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <></>
              )}
              <br></br>
              <span
                className="innerElement obituatyMainText"
                dangerouslySetInnerHTML={{
                  __html:
                    formattedString && formattedString !== ''
                      ? formattedString
                      : obituary?.main_text?.replace(new RegExp('\r?\n', 'g'), '<br />') || '',
                }}
              >
                {/* {formattedString && formattedString !== ''
                  ? formattedString
                  : obituary?.main_text.replace(/\n/g, '<br />')} */}
              </span>
              {/* <span style={{ marginTop: '20px' }} className="innerElement obituaryBereaved">
                {obituary?.bereaved}
              </span> */}
              <span
                style={{ marginTop: '20px' }}
                className="innerElement obituatyMainText"
                dangerouslySetInnerHTML={{
                  __html:
                    formattedStringBereaved && formattedStringBereaved !== ''
                      ? formattedStringBereaved
                      : obituary?.bereaved?.replace(new RegExp('\r?\n', 'g'), '<br />') || '',
                }}
              >
                {/* {formattedString && formattedString !== ''
                  ? formattedString
                  : obituary?.main_text.replace(/\n/g, '<br />')} */}
              </span>
              <span className="innerElement obiNum"></span>
            </div>
          </div>
          <div className="shareIcons">
            <div
              style={{
                width: '35px',
                height: '35px',
                background: '#eee',
                borderRadius: '9999px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <a
                href={`http://www.facebook.com/share.php?u=${shareLink}`}
                style={{ display: 'block', width: '20px', height: '20px', margin: 'auto' }}
                className="fb-special-margin"
              >
                <FacebookIcon></FacebookIcon>
              </a>
            </div>

            <div
              style={{
                width: '35px',
                height: '35px',
                background: '#eee',
                borderRadius: '9999px',
                marginLeft: '20px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <a
                href={`https://twitter.com/intent/tweet?text=${shareLink}`}
                style={{ display: 'block', width: '20px', height: '20px', margin: 'auto' }}
              >
                <TwitterIcon></TwitterIcon>
              </a>
            </div>
            <div
              style={{
                width: '35px',
                height: '35px',
                background: '#eee',
                borderRadius: '9999px',
                marginLeft: '20px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <a
                href={`https://api.whatsapp.com/send?text=${shareLink}`}
                style={{ display: 'block', width: '20px', height: '20px', margin: 'auto' }}
                className="fb-special-margin"
              >
                <WhatsAppIcon></WhatsAppIcon>
              </a>
            </div>
            <div
              style={{
                width: '35px',
                height: '35px',
                background: '#eee',
                borderRadius: '9999px',
                marginLeft: '20px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <a
                href={`viber://forward?text=${shareLink}`}
                style={{ display: 'block', width: '20px', height: '20px', margin: 'auto' }}
              >
                <ViberIcon></ViberIcon>
              </a>
            </div>
            {isBreakpoint && (
              <div
                style={{
                  width: '35px',
                  height: '35px',
                  background: '#eee',
                  borderRadius: '9999px',
                  marginLeft: '20px',
                  justifyContent: 'center',
                  alignContent: 'center',
                  display: 'flex',
                }}
              >
                <button
                  onClick={handleShare}
                  style={{ width: '40px', border: 'none', background: 'none' }}
                  id="btnHandleShare"
                >
                  <ShareIcon></ShareIcon>
                </button>
              </div>
            )}
            {!isBreakpoint && (
              <div
                style={{
                  width: '35px',
                  height: '35px',
                  background: '#eee',
                  borderRadius: '9999px',
                  marginLeft: '20px',
                }}
              >
                <button id="copyButton" onClick={handleClick}>
                  <LinkIcon></LinkIcon>
                </button>
                {message && message !== '' ? <div className="message-copy">{message}</div> : <></>}
              </div>
            )}
          </div>
        </div>
      )}
      {process.env.WIDGET === 'true' && (
        <div>
          <div style={{ backgroundColor: '#f9f9f9', maxWidth: '960px', margin: '0 auto' }}>
            <iframe
              src="https://frontend.mishima.development.avaz.ba/widget/1/5/light?source=valletta"
              id={'iframe-1'}
              title={'articleSliderPage'}
              scrolling="no"
              style={{
                width: '100%',
                border: 'none',
                //maxWidth: '1260px',
                height: '320px',
                overflow: 'hidden',
              }}
            ></iframe>
          </div>
        </div>
      )}
      <Footer></Footer>
    </div>
  );
};
